import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';

export interface LookupOption {
  label: string;
  value: string;
  contribution_count?: number;
  total_amount?: number;
  /** Carries endpoint-specific extras (e.g. vendor_id) through to onSelect. */
  meta?: Record<string, unknown>;
}

interface CFLookupFieldProps {
  endpoint: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Fired only on an explicit dropdown pick — the moment an IDENTITY is confirmed,
   * as opposed to free text. The vendor typeahead uses this to capture vendor_id.
   */
  onSelect?: (opt: LookupOption) => void;
  /** Adapts endpoints whose response body isn't already a LookupOption array. */
  transform?: (data: unknown) => LookupOption[];
  placeholder?: string;
  className?: string;
  icon?: React.ReactNode;
}

const CFLookupField: React.FC<CFLookupFieldProps> = ({
  endpoint,
  value,
  onChange,
  onSelect,
  transform,
  placeholder = 'Search...',
  className = '',
  icon,
}) => {
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();
  const containerRef = useRef<HTMLDivElement>(null);
  // Held in a ref so the debounce effect doesn't depend on it: transform is typically an
  // inline arrow in the parent, and a dep on it would re-fire the fetch on every render.
  const transformRef = useRef(transform);
  transformRef.current = transform;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim().length < 2) {
      setOptions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setSearching(true);
      try {
        const res = await api.get(endpoint, {
          params: { search: value.trim() },
          signal: controller.signal,
        });
        if (!controller.signal.aborted && res.data?.success) {
          const opts = transformRef.current ? transformRef.current(res.data.data) : (res.data.data || []);
          setOptions(opts);
          setOpen(opts.length > 0);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          setOptions([]);
        }
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, endpoint]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (opt: LookupOption) => {
    onChange(opt.value);
    onSelect?.(opt);
    setOpen(false);
    setOptions([]);
  };

  const fmtAmount = (n?: number) => {
    if (n == null) return '';
    return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        {icon && (
          <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
            {icon}
          </span>
        )}
        <input
          type="text"
          autoComplete="off"
          className={`input text-sm w-full ${icon ? 'pl-8' : ''}`}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => { if (options.length > 0) setOpen(true); }}
        />
        {searching && (
          <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-text-tertiary dark:text-text-dark-tertiary" />
          </span>
        )}
      </div>

      {open && options.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => handleSelect(opt)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary
                           text-text-primary dark:text-text-dark-primary transition-colors"
              >
                <span className="font-medium">{opt.label}</span>
                {(opt.contribution_count || opt.total_amount) && (
                  <span className="ml-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {opt.contribution_count ? `${opt.contribution_count} contributions` : ''}
                    {opt.contribution_count && opt.total_amount ? ' · ' : ''}
                    {opt.total_amount ? fmtAmount(opt.total_amount) : ''}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CFLookupField;
